import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {lazy} from '@aboutyou/lazy-imports';
import {
  COLORS,
  FixedRatioContainer,
  FullScreenOverlay,
  FullScreenOverlayChildrenProps,
  HorizontalSimpleSlider,
  LoadingPageSpinner,
  ProductImage,
  QUICK_VIEW_PRODUCT_SLIDER_PROGRESS_BAR_DISPLAY,
  StyledScrollbar,
  StyledSlider,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useRef, useState} from 'react';
import {useInlineSizeSelectionQuickViewAB} from '../AddToBasketQuickView/hooks/useInlineSizeSelectionQuickViewAB';
import {SlideContainer, SliderContent} from '../ProductSlider/SliderContent';
import {PRODUCT_ZOOM_KEY} from '../utils';

const ProductImageZoom = lazy(
  () =>
    import(
      '@aboutyou/ui-components/src/components/molecules/ProductImageZoom/ProductImageZoom'
    ).then(mod => ({default: mod.ProductImageZoom})),
  {
    fallback: <LoadingPageSpinner />,
  },
);

export interface ProductSliderQuickViewViewProps {
  isDefaultState?: boolean;
  goBack: React.ReactNode;
  setShowSimilarItemsButton: (setShouldShowSimilarItems: boolean) => void;
  discoverAlternativesButton?: React.ReactNode;
}

export const ProductSliderQuickViewView: React.FC<
  GetProductBulkResponse['imagesSection'] &
    ProductSliderQuickViewViewProps & {
      isDefaultState?: boolean;
    }
> = ({
  product,
  isDefaultState,
  goBack,
  setShowSimilarItemsButton,
  discoverAlternativesButton,
  gallery,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isInlineSizeSelectionQuickViewAB = useInlineSizeSelectionQuickViewAB();

  const hasBeenInView = useRef(false);
  const {ref} = useInViewEffect<HTMLDivElement>(
    inView => {
      if (inView && !hasBeenInView.current) {
        hasBeenInView.current = true;
        setShowSimilarItemsButton(true);
      }
    },
    {
      triggerOnce: true,
      threshold: 0.3,
    },
  );

  if (product?.isSoldOut) {
    return null;
  }

  return (
    <ProductSliderQuickViewViewWrapper ref={ref}>
      <StyledFixedRatioContainer
        ratio="3:4"
        $singleImgProduct={gallery.elements.length === 1}
        $largerGallery={isInlineSizeSelectionQuickViewAB}
      >
        <ImageFullScreenOverlay
          openStateKey={PRODUCT_ZOOM_KEY}
          isOpenByDefault={false}
          dataTestId="ProductSliderFullscreen"
          renderInPortal={false}
        >
          {({open, isOpen}: FullScreenOverlayChildrenProps) => (
            <StyledHorizontalSimpleSlider
              showNavButtons={isOpen}
              navOverSlider={isOpen}
              shouldReduceSliderHeight={!isOpen}
              currentSlideIndicator={!isOpen ? 'scrollbar' : 'bullets'}
              dataTestId="productSlider"
              key={isOpen ? 'open' + currentSlide : 'closed'}
              initialSlide={currentSlide}
              uniqueKey="productSliderQuickViewView"
              data-is-open={isOpen}
            >
              <SliderContent
                isOpen={Boolean(isOpen)}
                imageComponent={
                  (isOpen ? ProductImageZoom : ProductImage) as any
                }
                activeIndex={currentSlide}
                isLoadingState={isDefaultState}
                ratio="3:4"
                columns={isOpen ? 1 : 2}
                hideSTL
                gallery={gallery}
                onSlideClick={index => {
                  setCurrentSlide(index);
                  open();
                }}
              />
            </StyledHorizontalSimpleSlider>
          )}
        </ImageFullScreenOverlay>
        {goBack}
      </StyledFixedRatioContainer>
      {discoverAlternativesButton}
    </ProductSliderQuickViewViewWrapper>
  );
};

const ProductSliderQuickViewViewWrapper = styled.div`
  position: relative;
`;

const StyledFixedRatioContainer = styled(FixedRatioContainer)<{
  $singleImgProduct: boolean;
  $largerGallery?: boolean;
}>`
  // this prevents the ripples on CTAs to cause the component to overflow horizontally
  overflow: hidden;
  position: relative;

  --height-calculation-unit: 100svh;
  @supports not (height: 100svh) {
    // iOS < 15.4. does not support sv-units
    --height-calculation-unit: 100vh;
    // This is an approximate value of the address- and navigation bar height on iOS < 15.4. since svh is not working there and vh does not take these bars into account
    --additional-height-reduction: 80px;
  }

  --image-gallery-height: clamp(
    330px,
    ${props => (props.$largerGallery ? '40svh' : '31svh')},
    400px
  );
  @supports not (height: 100svh) {
    --image-gallery-height: clamp(
      330px,
      ${props => (props.$largerGallery ? '40vh' : '31vh')},
      400px
    );
  }

  height: var(--image-gallery-height);
  width: 100%;
  padding: 0;

  ${SlideContainer} {
    height: var(--image-gallery-height);
    min-width: calc(var(--image-gallery-height) * (3 / 4));
    scroll-snap-align: start;

    margin-inline: ${props => (props.$singleImgProduct ? 'auto' : '0')};
  }

  ${StyledSlider} {
    gap: 2px;
    background-color: ${props =>
      props.$singleImgProduct ? COLORS.ProductImageBackground : 'none'};
  }
`;

const ImageFullScreenOverlay = styled(FullScreenOverlay)`
  display: flex;
  flex-direction: column;
`;

const StyledHorizontalSimpleSlider = styled(HorizontalSimpleSlider)`
  ${StyledScrollbar} {
    display: var(${QUICK_VIEW_PRODUCT_SLIDER_PROGRESS_BAR_DISPLAY}, initial);
  }

  &[data-is-open='true'] {
    ${SlideContainer} {
      height: auto;
      min-width: 100%;
    }
  }
`;
